import { useEffect, useState } from 'react';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function isLeapYear(y: number) {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(month: number, year: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if ([4, 6, 9, 11].includes(month)) return 30;
  return 31;
}

function parseValue(value: string): { day: string; month: string; year: string } {
  if (!value || value.length !== 10) return { day: '', month: '', year: '' };
  const parts = value.split('-');
  if (parts.length !== 3) return { day: '', month: '', year: '' };
  const [y, m, d] = parts;
  const yi = parseInt(y, 10);
  const mi = parseInt(m, 10);
  const di = parseInt(d, 10);
  if (isNaN(yi) || isNaN(mi) || isNaN(di)) return { day: '', month: '', year: '' };
  return { day: String(di), month: String(mi), year: String(yi) };
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS: number[] = [];
for (let y = CURRENT_YEAR; y >= CURRENT_YEAR - 120; y--) YEARS.push(y);

interface Props {
  value: string;        // YYYY-MM-DD or ""
  onChange: (value: string) => void;
  required?: boolean;
  style?: React.CSSProperties;
}

export function BirthDateInput({ value, onChange, required, style }: Props) {
  const parsed = parseValue(value);
  const [day,   setDay]   = useState(parsed.day);
  const [month, setMonth] = useState(parsed.month);
  const [year,  setYear]  = useState(parsed.year);

  useEffect(() => {
    const p = parseValue(value);
    setDay(p.day);
    setMonth(p.month);
    setYear(p.year);
  }, [value]);

  function emit(d: string, m: string, y: string) {
    if (!d || !m || !y) { onChange(''); return; }
    const di = parseInt(d, 10);
    const mi = parseInt(m, 10);
    const yi = parseInt(y, 10);
    const maxDays = daysInMonth(mi, yi);
    const validDay = Math.min(di, maxDays);
    if (validDay !== di) setDay(String(validDay));
    const mm = String(mi).padStart(2, '0');
    const dd = String(validDay).padStart(2, '0');
    onChange(`${y}-${mm}-${dd}`);
  }

  function handleDay(d: string) {
    setDay(d);
    emit(d, month, year);
  }

  function handleMonth(m: string) {
    setMonth(m);
    emit(day, m, year);
  }

  function handleYear(y: string) {
    setYear(y);
    emit(day, month, y);
  }

  const selectedMonth = parseInt(month, 10) || 1;
  const selectedYear  = parseInt(year, 10) || CURRENT_YEAR;
  const maxDays       = daysInMonth(selectedMonth, selectedYear);

  const selectStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.35rem 0.25rem',
    fontSize: 'var(--text-body)',
  };

  return (
    <div style={{ display: 'flex', gap: '0.4rem', ...style }}>
      {/* Day */}
      <select
        value={day}
        onChange={e => handleDay(e.target.value)}
        className="form-input"
        required={required}
        aria-label="Dia"
        style={{ ...selectStyle, width: '4.5rem', flexGrow: 0 }}
      >
        <option value="">Dia</option>
        {Array.from({ length: maxDays }, (_, i) => i + 1).map(d => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>

      {/* Month */}
      <select
        value={month}
        onChange={e => handleMonth(e.target.value)}
        className="form-input"
        required={required}
        aria-label="Mês"
        style={{ ...selectStyle, minWidth: '7rem' }}
      >
        <option value="">Mês</option>
        {MONTHS.map((m, i) => (
          <option key={i + 1} value={i + 1}>{m}</option>
        ))}
      </select>

      {/* Year */}
      <select
        value={year}
        onChange={e => handleYear(e.target.value)}
        className="form-input"
        required={required}
        aria-label="Ano"
        style={{ ...selectStyle, width: '5.5rem', flexGrow: 0 }}
      >
        <option value="">Ano</option>
        {YEARS.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}
